import { Controller } from "@hotwired/stimulus"

// Tracking marketing des pages publiques BEB (landing + /lancement).
// Posé sur le wrapper de page :
//   <div data-controller="beb-track" data-beb-track-page-value="landing">
//
// Émet vers POST /t/beb :
//  - page_view (référent + UTM + device) au connect
//  - scroll (25/50/75/100, une fois chacun)
//  - cta_click sur tout élément portant data-track-cta="nom-du-cta"
//  - presence (secondes à l'écran depuis le dernier envoi) quand la page
//    passe à l'arrière-plan ou se ferme. La somme par visiteur est son temps
//    sur la page ; un onglet resté ouvert sans être regardé ne compte pas.
//  - relaie les CustomEvent "beb:track" (utilisés par lead-funnel pour les étapes)
export default class extends Controller {
  static values = { page: String, meta: Object }

  connect() {
    this.visitorId = this.ensureVisitorId()
    this.sentDepths = new Set()

    const qs = new URLSearchParams(window.location.search)
    this.send("page_view", {
      referrer: document.referrer || "",
      device: /Mobi|Android|iPhone|iPad/i.test(navigator.userAgent) ? "mobile" : "desktop",
      utm_source: qs.get("utm_source") || "",
      utm_medium: qs.get("utm_medium") || "",
      utm_campaign: qs.get("utm_campaign") || "",
      utm_content: qs.get("utm_content") || "",
      ad_id: qs.get("ad_id") || "",
      ...this.metaValue
    })

    this.onScroll = this.trackScroll.bind(this)
    window.addEventListener("scroll", this.onScroll, { passive: true })

    this.onClick = (e) => {
      const el = e.target.closest("[data-track-cta]")
      if (el) this.send("cta_click", { cta: el.dataset.trackCta })
    }
    document.addEventListener("click", this.onClick, true)

    this.onCustom = (e) => this.send(e.detail.event_type, e.detail.meta || {})
    window.addEventListener("beb:track", this.onCustom)

    this.visibleDepuis = document.visibilityState === "visible" ? Date.now() : null
    this.onVisibility = () => {
      if (document.visibilityState === "visible") this.visibleDepuis = this.visibleDepuis || Date.now()
      else this.sendPresence()
    }
    this.onPageHide = () => this.sendPresence()
    document.addEventListener("visibilitychange", this.onVisibility)
    window.addEventListener("pagehide", this.onPageHide)
  }

  disconnect() {
    window.removeEventListener("scroll", this.onScroll)
    document.removeEventListener("click", this.onClick, true)
    window.removeEventListener("beb:track", this.onCustom)
    document.removeEventListener("visibilitychange", this.onVisibility)
    window.removeEventListener("pagehide", this.onPageHide)
    this.sendPresence()
  }

  // Envoie le temps écoulé depuis le dernier envoi, puis repart de zéro :
  // visibilitychange et pagehide arrivent souvent tous les deux, et le
  // second ne doit rien recompter. Plafonné à 30 minutes par envoi.
  sendPresence() {
    if (!this.visibleDepuis) return
    const seconds = Math.min(Math.round((Date.now() - this.visibleDepuis) / 1000), 1800)
    this.visibleDepuis = null
    if (seconds >= 1) this.send("presence", { seconds: seconds })
  }

  trackScroll() {
    const height = document.documentElement.scrollHeight - window.innerHeight
    if (height <= 0) return
    const pct = Math.round((window.scrollY / height) * 100)
    for (const depth of [25, 50, 75, 100]) {
      if (pct >= depth && !this.sentDepths.has(depth)) {
        this.sentDepths.add(depth)
        this.send("scroll", { depth: depth })
      }
    }
  }

  send(eventType, meta) {
    const body = JSON.stringify({
      event_type: eventType,
      page: this.pageValue,
      visitor_id: this.visitorId,
      meta: { ...meta, ...this.metaValue }
    })
    if (navigator.sendBeacon) {
      navigator.sendBeacon("/t/beb", new Blob([body], { type: "application/json" }))
    } else {
      fetch("/t/beb", { method: "POST", headers: { "Content-Type": "application/json" }, body: body, keepalive: true }).catch(() => {})
    }
  }

  // La clé vit à deux endroits. Le localStorage gagne, parce qu'il porte
  // l'historique des visiteurs d'avant le 17/09/2026 ; le cookie est réécrit
  // avec la même valeur, parce que c'est lui que le serveur relit à
  // l'inscription (EnregistreLeParcours) pour rendre ces événements anonymes
  // au compte créé ensuite. Le localStorage seul ne quittait jamais le navigateur.
  ensureVisitorId() {
    const KEY = "beb_vid"
    const fromCookie = decodeURIComponent((document.cookie.match(/(?:^|; )beb_vid=([^;]+)/) || [])[1] || "")
    let id = ""
    try { id = localStorage.getItem(KEY) || "" } catch (e) { /* stockage bloqué */ }
    id = id || fromCookie || (crypto.randomUUID ? crypto.randomUUID() : String(Math.random()).slice(2) + Date.now())
    try { localStorage.setItem(KEY, id) } catch (e) { /* stockage bloqué */ }
    if (id !== fromCookie) {
      const secure = location.protocol === "https:" ? "; Secure" : ""
      document.cookie = `beb_vid=${encodeURIComponent(id)}; Max-Age=31536000; Path=/; SameSite=Lax${secure}`
    }
    return id
  }

}
